import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import RegisterUserService from "../../../Admin/services/RegisterUserService";
import {
  SaPageShell,
  SaPageHeader,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";

function EditClientUser() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    contactNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    panNumber: "",
    userImage: null,
    passportNumber: "",
    aadharNumber: "",
    isActive: false,
    id,
    clientId: "",
  });
  const [formError, setFormError] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchUserData = async () => {
      try {
        const user = await RegisterUserService.getUserById(id);
        if (!cancelled) {
          setForm((prev) => ({
            ...prev,
            ...user,
            password: "",
            id,
          }));
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load user.",
          life: 4000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUserData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    ["contactNumber", "streetAddress", "city", "state", "country", "postalCode"].forEach((key) => {
      if (!String(form[key] ?? "").trim()) {
        errors[key] = "Required";
      }
    });
    if (!String(form.clientId ?? "").trim()) {
      errors.clientId = "Missing client assignment.";
    }
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Fill required fields.",
        life: 3000,
      });
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(form).forEach((key) => {
      const val = form[key];
      if (key === "password" && (!val || String(val).trim() === "")) {
        return;
      }
      if (val !== undefined && val !== null) {
        if (typeof val === "boolean") {
          formDataToSend.append(key, val ? "true" : "false");
        } else {
          formDataToSend.append(key, val);
        }
      }
    });

    setSaving(true);
    try {
      await RegisterUserService.updateUser(id, formDataToSend);
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "User updated successfully.",
        life: 2800,
      });
      setTimeout(() => navigate(-1), 600);
    } catch (error) {
      console.error("Error updating user:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update user.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const userImage = reader.result.split(",")[1];
      setForm((prev) => ({ ...prev, userImage }));
    };
    reader.onerror = () => console.error("File read error");
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  const err = (name) =>
    formError[name] ? (
      <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError[name]}</p>
    ) : null;

  const readOnlyStyle = {
    ...saInputSx,
    background: SA.bgSoft,
    color: SA.muted,
  };

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title="Edit user"
        subtitle={loading ? "Loading…" : `${form.firstName} ${form.lastName} · ID ${id}`}
        actions={
          <SaGhostButton type="button" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
            Back
          </SaGhostButton>
        }
      />

      <form onSubmit={onSubmit}>
        <SaCard>
          <SaSectionTitle iconClass="fas fa-user">Account</SaSectionTitle>
          <p style={{ margin: "-8px 0 16px", fontSize: "13px", color: SA.muted }}>
            Name and email are read-only here; update password only if you want to reset it. Client ID cannot be changed from this screen.
          </p>
          <div style={grid}>
            <SaField label="First name">
              <input type="text" name="firstName" value={form.firstName} readOnly style={readOnlyStyle} />
            </SaField>
            <SaField label="Last name">
              <input type="text" name="lastName" value={form.lastName} readOnly style={readOnlyStyle} />
            </SaField>
            <SaField label="Email">
              <input type="email" name="email" value={form.email} readOnly style={readOnlyStyle} />
            </SaField>
            <SaField label="New password" hint="Leave blank to keep current password.">
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                value={form.password}
                onChange={handleInputChange}
                style={saInputSx}
              />
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-address-book">Contact &amp; address *</SaSectionTitle>
          <div style={grid}>
            <SaField label="Contact number *">
              <input
                name="contactNumber"
                value={form.contactNumber}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.contactNumber ? SA.danger : SA.border }}
              />
              {err("contactNumber")}
            </SaField>
            <SaField label="Street address *">
              <input
                name="streetAddress"
                value={form.streetAddress}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.streetAddress ? SA.danger : SA.border }}
              />
              {err("streetAddress")}
            </SaField>
            <SaField label="City *">
              <input name="city" value={form.city} onChange={handleInputChange} style={{ ...saInputSx, borderColor: formError.city ? SA.danger : SA.border }} />
              {err("city")}
            </SaField>
            <SaField label="State *">
              <input name="state" value={form.state} onChange={handleInputChange} style={{ ...saInputSx, borderColor: formError.state ? SA.danger : SA.border }} />
              {err("state")}
            </SaField>
            <SaField label="Country *">
              <input name="country" value={form.country} onChange={handleInputChange} style={{ ...saInputSx, borderColor: formError.country ? SA.danger : SA.border }} />
              {err("country")}
            </SaField>
            <SaField label="Postal code *">
              <input
                type="text"
                name="postalCode"
                value={form.postalCode}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.postalCode ? SA.danger : SA.border }}
              />
              {err("postalCode")}
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-id-card">Identification</SaSectionTitle>
          <div style={grid}>
            <SaField label="PAN">
              <input type="text" name="panNumber" value={form.panNumber ?? ""} readOnly style={readOnlyStyle} />
            </SaField>
            <SaField label="Passport">
              <input type="text" name="passportNumber" value={form.passportNumber ?? ""} readOnly style={readOnlyStyle} />
            </SaField>
            <SaField label="Aadhar">
              <input type="text" name="aadharNumber" value={form.aadharNumber ?? ""} onChange={handleInputChange} style={saInputSx} />
            </SaField>
            <SaField label="Photo">
              <input type="file" accept="image/*" onChange={onFileChange} style={{ fontSize: "13px", color: SA.muted }} />
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-sliders-h">Status</SaSectionTitle>
          <div style={{ ...grid, alignItems: "center" }}>
            <SaField label="Active">
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
                <input type="checkbox" name="isActive" checked={!!form.isActive} onChange={handleInputChange} style={{ width: "18px", height: "18px" }} />
                User can sign in
              </label>
            </SaField>
            <SaField label="Client ID" hint="Read-only — move users between agencies only via support / admin tools.">
              <input name="clientId" value={form.clientId} readOnly style={readOnlyStyle} />
              {err("clientId")}
            </SaField>
          </div>

          <div
            style={{
              marginTop: "28px",
              paddingTop: "22px",
              borderTop: `1px solid ${SA.border}`,
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <SaPrimaryButton type="submit" disabled={saving || loading} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-save"}>
              {saving ? "Saving…" : "Update user"}
            </SaPrimaryButton>
            <SaGhostButton type="button" onClick={() => navigate(-1)}>
              Cancel
            </SaGhostButton>
          </div>
        </SaCard>
      </form>
    </SaPageShell>
  );
}

export default EditClientUser;
